import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';

import {
  Rarity,
  ValueBoundaries,
} from '@sorare/core/src/__generated__/globalTypes';
import { NewScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Bold } from '@sorare/core/src/atoms/typography/Bold';
import { CamelCaseScarcity, scarcityNames } from '@sorare/core/src/lib/cards';
import { withFragments } from '@sorare/core/src/lib/gql';
import { sortByRarity } from '@sorare/core/src/lib/scarcity';
import { toCamelCase } from '@sorare/core/src/lib/toCamelCase';

import { RuleHelperFnReturnType } from 'components/so5/Rules/types';
import {
  EngineConfiguration,
  ScoreModifier,
  getEngineConfigurationScarcities,
  getScoreModifiers,
} from 'lib/so5';

import { getRarityLimitsRule_so5Leaderboard } from './__generated__/getRarityLimitsRule.graphql';

const messages = defineMessages({
  title: {
    id: 'Rules.rarityLimits.title',
    defaultMessage: `{display, select,
        isAny {Any number of <b>{rarity}</b> Cards}
        isExact {{max}x <b>{rarity}</b> {max, plural, one {Card} other {Cards}}}
        isMax {Max {max}x <b>{rarity}</b> {max, plural, one {Card} other {Cards}}}
        isMin {Min {min}x <b>{rarity}</b> {min, plural, one {Card} other {Cards}}}
        other {Between {min} and {max} <b>{rarity}</b> Cards}
    }`,
  },
  higherScarcitiesPermitted: {
    id: 'Rules.rarityLimits.higherScarcitiesPermitted',
    defaultMessage: 'Higher scarcities permitted',
  },
  score: {
    id: 'Rules.rarityLimits.description',
    defaultMessage: `{score, select,
      noPoints { Card scores 0 points}
      less { Card score decreased by {scoreModifier, number, percent}}
      more { Card score increased by {scoreModifier, number, percent}}
      other {}
    }`,
  },
  descriptionCommonFtue: {
    id: 'Rules.rarityLimits.descriptionCommonFtue',
    defaultMessage: "You don't own it{br}You can't sell it on the market",
  },
  descriptionBlockchainFtue: {
    id: 'Rules.rarityLimits.descriptionBlockchainFtue',
    defaultMessage:
      'You own it{br}You can sell it on the market{br}You can play it through the seasons',
  },
});

const SUPPORTED_SCARCITIES = [
  Rarity.unique,
  Rarity.super_rare,
  Rarity.rare,
  Rarity.limited,
  Rarity.common,
] as const;

type SupportedScarcities = (typeof SUPPORTED_SCARCITIES)[number];

const rarityToCamelCaseRarity = (rarity: Rarity): CamelCaseScarcity =>
  toCamelCase(rarity) as CamelCaseScarcity;

export const getRarityLimitsRule = withFragments(
  (
    so5Leaderboard: getRarityLimitsRule_so5Leaderboard,
    error: string,
    ftue?: boolean
  ): RuleHelperFnReturnType => {
    const { engineConfiguration, displayedRules } = so5Leaderboard;
    const rule = displayedRules?.rarityLimits;
    const maxAppearancesCount = displayedRules?.appearances?.length || 5;

    const scarcities = getEngineConfigurationScarcities(
      engineConfiguration?.scarcity as EngineConfiguration['scarcity']
    );

    let simpleRules: {
      rarity: Rarity;
      min: number;
      max: number;
      higherAllowed?: boolean;
    }[] = [];

    const cleanedRule = Object.fromEntries(
      SUPPORTED_SCARCITIES.map(rarity => [
        rarity,
        rule?.[toCamelCase(rarity)] || null,
      ])
    ) as Record<SupportedScarcities, ValueBoundaries | null>;

    const raritiesWithRule = SUPPORTED_SCARCITIES.slice()
      .map(rarity => cleanedRule[rarity])
      .filter(Boolean)
      .filter(({ min, max }) => min || max);

    const uniqueLimits = cleanedRule[Rarity.unique];
    const uniqueHasNoLimits =
      uniqueLimits?.min === 0 && uniqueLimits?.max === maxAppearancesCount;
    const lowerRarities = SUPPORTED_SCARCITIES.slice(1);

    // Get every rarities with min/max not limiting
    const hasNoLimitsByRarity = lowerRarities.map(rarity => {
      const rarityLimit: ValueBoundaries | null = cleanedRule[rarity];
      if (!rarityLimit) {
        return null;
      }
      return rarityLimit.min === 0 && rarityLimit.max === maxAppearancesCount;
    });

    // Get lowest consecutive rarity with no min/max limiting
    const lowerAdjactRarityWithNoLimits = uniqueHasNoLimits
      ? hasNoLimitsByRarity.findIndex((rarity, i, array) => !array[i + 1])
      : null;

    if (lowerAdjactRarityWithNoLimits !== null) {
      const adjacentRarities = SUPPORTED_SCARCITIES.slice(
        0,
        lowerAdjactRarityWithNoLimits + 2
      );

      if (
        adjacentRarities.length > 1 &&
        adjacentRarities.length === raritiesWithRule.length
      ) {
        // Check if consecutive rarities share same rarity bonus
        const firstAdjacentRarity = adjacentRarities[0];
        const firstAdjacentRarityBonus =
          scarcities[rarityToCamelCaseRarity(firstAdjacentRarity)];
        const adjacentRaritiesHasSameBonus = adjacentRarities.every(
          rarity =>
            scarcities[rarityToCamelCaseRarity(rarity)] ===
            firstAdjacentRarityBonus
        );

        if (adjacentRaritiesHasSameBonus) {
          // Create single rule targeting lower consecutive rarity with higherAllowed
          simpleRules = [
            {
              rarity: adjacentRarities[adjacentRarities.length - 1],
              min: 0,
              max: maxAppearancesCount,
              higherAllowed: true,
            },
          ];
        }
      }
    }

    // Normalize rarity limits of non consecutive rarities
    if (!simpleRules.length) {
      simpleRules = sortByRarity(SUPPORTED_SCARCITIES.slice())
        .reverse()
        .map((rarity, i, array) => {
          const rarityLimits = cleanedRule[rarity];
          if (!rarityLimits) {
            delete array[i];
            return null;
          }

          return {
            rarity,
            ...rarityLimits,
          };
        })
        .filter(Boolean)
        .filter(({ min, max }) => min || max);
    }

    // Format rarity limits rules
    return simpleRules.map(({ rarity, min, max, higherAllowed }) => {
      const { score, scoreModifier } = getScoreModifiers(
        scarcities[rarityToCamelCaseRarity(rarity)]
      );

      const hasOnlyOneRule = simpleRules.length === 1;
      const isAnyNumberOfCards = min === 0 && max === maxAppearancesCount;

      let display = '';
      if ((hasOnlyOneRule && isAnyNumberOfCards) || min === max) {
        display = 'isExact';
      } else if (min === 0 && max === maxAppearancesCount) {
        display = 'isAny';
      } else if (min === 0) {
        display = 'isMax';
      } else if (max === maxAppearancesCount) {
        display = 'isMin';
      }

      const descriptionFtue =
        rarity === Rarity.common
          ? messages.descriptionCommonFtue
          : messages.descriptionBlockchainFtue;

      const hasDescription =
        ftue || higherAllowed || score !== ScoreModifier.None;

      const description = ftue ? (
        <FormattedMessage
          {...descriptionFtue}
          values={{
            br: <br />,
          }}
        />
      ) : (
        <>
          {higherAllowed && (
            <FormattedMessage {...messages.higherScarcitiesPermitted} />
          )}
          {higherAllowed && score !== ScoreModifier.None && '.'}
          {score !== ScoreModifier.None && (
            <FormattedMessage
              {...messages.score}
              values={{
                score,
                scoreModifier: Math.abs(scoreModifier),
              }}
            />
          )}
        </>
      );

      return {
        id: `rarityLimits_${rarity}`,
        lowestRarity: rarity,
        error,
        label: (
          <FormattedMessage
            {...messages.title}
            values={{
              b: Bold,
              min,
              max,
              rarity: scarcityNames[rarity],
              display,
            }}
          />
        ),
        description: hasDescription ? description : null,
        icon: <NewScarcityIcon rarity={rarity} size={2} />,
      };
    });
  },
  {
    so5Leaderboard: gql`
      fragment getRarityLimitsRule_so5Leaderboard on So5Leaderboard {
        slug
        displayedRules {
          id
          rarityLimits {
            common {
              min
              max
            }
            limited {
              min
              max
            }
            rare {
              min
              max
            }
            superRare {
              min
              max
            }
            unique {
              min
              max
            }
          }
          appearances {
            name
          }
        }
        engineConfiguration {
          scarcity
        }
      }
    ` as TypedDocumentNode<getRarityLimitsRule_so5Leaderboard>,
  }
);
